import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { BookmarkedRoute, Itinerary } from '../types/ferry';
import { RouteResult } from '../components/RouteResults';
import { BookmarkService } from '../services/bookmarkService';
import { analytics } from '../utils/analytics';

interface BookmarkContextType {
  bookmarks: BookmarkedRoute[];
  itineraries: Itinerary[];
  
  // Bookmark operations
  addBookmark: (routeResult: RouteResult, selectedDate: string, customName?: string) => BookmarkedRoute;
  removeBookmark: (bookmarkId: string) => void;
  updateBookmark: (bookmarkId: string, updates: Partial<BookmarkedRoute>) => void;
  reorderBookmarks: (newOrder: BookmarkedRoute[]) => void;
  isBookmarked: (routeResult: RouteResult, selectedDate: string) => BookmarkedRoute | null;
  
  // Itinerary operations
  createItinerary: (name: string, routes?: BookmarkedRoute[]) => Itinerary;
  updateItinerary: (itineraryId: string, updates: Partial<Itinerary>) => void;
  deleteItinerary: (itineraryId: string) => void;
  addRouteToItinerary: (itineraryId: string, bookmark: BookmarkedRoute) => void;
  removeRouteFromItinerary: (itineraryId: string, bookmarkId: string) => void;
  
  // Utility operations
  exportData: () => string;
  importData: (jsonData: string) => boolean;
  clearAllData: () => void;
  
  // UI state
  showItineraryPanel: boolean;
  setShowItineraryPanel: (show: boolean) => void;
}

const BookmarkContext = createContext<BookmarkContextType | undefined>(undefined);

interface BookmarkProviderProps {
  children: ReactNode;
}

export const BookmarkProvider: React.FC<BookmarkProviderProps> = ({ children }) => {
  const [bookmarks, setBookmarks] = useState<BookmarkedRoute[]>([]);
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [showItineraryPanel, setShowItineraryPanel] = useState(false);

  // Load data from localStorage on mount
  useEffect(() => {
    setBookmarks(BookmarkService.getBookmarks());
    setItineraries(BookmarkService.getItineraries());
  }, []);

  // Bookmark operations
  const addBookmark = (routeResult: RouteResult, selectedDate: string, customName?: string): BookmarkedRoute => {
    const bookmark = BookmarkService.saveBookmark(routeResult, selectedDate, customName);
    setBookmarks(BookmarkService.getBookmarks());
    analytics.bookmarkAction('add', `${routeResult.fromStop.name} → ${routeResult.toStop.name}`);
    return bookmark;
  };

  const removeBookmark = (bookmarkId: string): void => {
    BookmarkService.removeBookmark(bookmarkId);
    setBookmarks(BookmarkService.getBookmarks());
    analytics.bookmarkAction('remove');
    
    // Also remove from all itineraries
    itineraries.forEach(itinerary => {
      const hasRoute = itinerary.routes.some(route => route.id === bookmarkId);
      if (hasRoute) {
        const updatedRoutes = itinerary.routes.filter(route => route.id !== bookmarkId);
        BookmarkService.updateItinerary(itinerary.id, { routes: updatedRoutes });
      }
    });
    setItineraries(BookmarkService.getItineraries());
  };

  const updateBookmark = (bookmarkId: string, updates: Partial<BookmarkedRoute>): void => {
    BookmarkService.updateBookmark(bookmarkId, updates);
    setBookmarks(BookmarkService.getBookmarks());
    
    // Update in itineraries as well
    itineraries.forEach(itinerary => {
      const routeIndex = itinerary.routes.findIndex(route => route.id === bookmarkId);
      if (routeIndex !== -1) {
        const updatedRoutes = [...itinerary.routes];
        updatedRoutes[routeIndex] = { ...updatedRoutes[routeIndex], ...updates };
        BookmarkService.updateItinerary(itinerary.id, { routes: updatedRoutes });
      }
    });
    setItineraries(BookmarkService.getItineraries());
  };

  const reorderBookmarks = (newOrder: BookmarkedRoute[]): void => {
    BookmarkService.reorderBookmarks(newOrder);
    setBookmarks(BookmarkService.getBookmarks());
    analytics.bookmarkAction('reorder');
  };

  const isBookmarked = (routeResult: RouteResult, selectedDate: string): BookmarkedRoute | null => {
    return BookmarkService.isBookmarked(routeResult, selectedDate);
  };

  // Itinerary operations
  const createItinerary = (name: string, routes: BookmarkedRoute[] = []): Itinerary => {
    const itinerary = BookmarkService.createItinerary(name, routes);
    setItineraries(BookmarkService.getItineraries());
    return itinerary;
  };

  const updateItinerary = (itineraryId: string, updates: Partial<Itinerary>): void => {
    BookmarkService.updateItinerary(itineraryId, updates);
    setItineraries(BookmarkService.getItineraries());
  };

  const deleteItinerary = (itineraryId: string): void => {
    BookmarkService.deleteItinerary(itineraryId);
    setItineraries(BookmarkService.getItineraries());
  };

  const addRouteToItinerary = (itineraryId: string, bookmark: BookmarkedRoute): void => {
    const itinerary = itineraries.find(it => it.id === itineraryId);
    if (itinerary) {
      const routes = [...itinerary.routes, bookmark];
      BookmarkService.updateItinerary(itineraryId, { routes });
      setItineraries(BookmarkService.getItineraries());
    }
  };

  const removeRouteFromItinerary = (itineraryId: string, bookmarkId: string): void => {
    const itinerary = itineraries.find(it => it.id === itineraryId);
    if (itinerary) {
      const routes = itinerary.routes.filter(route => route.id !== bookmarkId);
      BookmarkService.updateItinerary(itineraryId, { routes });
      setItineraries(BookmarkService.getItineraries());
    }
  };

  // Utility operations
  const exportData = (): string => {
    return BookmarkService.exportBookmarks();
  };

  const importData = (jsonData: string): boolean => {
    const success = BookmarkService.importBookmarks(jsonData);
    if (success) {
      setBookmarks(BookmarkService.getBookmarks());
      setItineraries(BookmarkService.getItineraries());
    }
    return success;
  };

  const clearAllData = (): void => {
    BookmarkService.clearAllData();
    setBookmarks([]);
    setItineraries([]);
  };

  const contextValue: BookmarkContextType = {
    bookmarks,
    itineraries,
    addBookmark,
    removeBookmark,
    updateBookmark,
    reorderBookmarks,
    isBookmarked,
    createItinerary,
    updateItinerary,
    deleteItinerary,
    addRouteToItinerary,
    removeRouteFromItinerary,
    exportData,
    importData,
    clearAllData,
    showItineraryPanel,
    setShowItineraryPanel,
  };

  return (
    <BookmarkContext.Provider value={contextValue}>
      {children}
    </BookmarkContext.Provider>
  );
};

export const useBookmark = () => {
  const context = useContext(BookmarkContext);
  if (context === undefined) {
    throw new Error('useBookmark must be used within a BookmarkProvider');
  }
  return context;
};